import { z } from "zod";
import { UuidSchema, TimestampsSchema, DateTimeSchema } from "./common.js";
import { IconNameSchema } from "./icon.js";
export const CampaignStatusSchema = z.enum([
    "draft",
    "pending",
    "approved",
    "active",
    "paused",
    "completed",
]);
export const LinkedCreativeSchema = z.object({
    uuid: UuidSchema,
    name: z.string().nullable(),
    url: z.string(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    durationSeconds: z.number().nullable(),
});
export const CampaignBaseSchema = z
    .object({
    uuid: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    selectedSegments: z.array(UuidSchema),
    userId: UuidSchema.nullable(),
    frequency: z.number(),
    defaultCreativeId: UuidSchema.nullable(),
    startDate: DateTimeSchema.nullable(),
    endDate: DateTimeSchema.nullable(),
    icon: IconNameSchema.nullable(),
    cpm: z.number(),
    dailyFrequency: z.number().int().nullable(),
    weeklyFrequency: z.number().int().nullable(),
    impressionsCap: z.number().int().nullable(),
    budgetCap: z.number().nullable(),
    impressionDomain: z.string().nullable().optional(),
    creatives: z.array(LinkedCreativeSchema).optional(),
})
    .merge(TimestampsSchema);
export const SegmentCreativesMapSchema = z.record(UuidSchema, UuidSchema);
export const CreateCampaignSchema = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    selectedSegments: z.array(UuidSchema),
    frequency: z.number().int().positive().optional().default(1),
    defaultCreativeId: UuidSchema.nullable().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    icon: IconNameSchema.nullable().optional(),
    cpm: z.number().min(0).optional().default(0),
    dailyFrequency: z.number().int().positive().nullable().optional(),
    weeklyFrequency: z.number().int().positive().nullable().optional(),
    impressionsCap: z.number().int().positive().nullable().optional(),
    budgetCap: z.number().min(0).nullable().optional(),
    segmentCreatives: SegmentCreativesMapSchema.optional(),
});
export const UpdateCampaignSchema = z.object({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    selectedSegments: z.array(UuidSchema).optional(),
    frequency: z.number().int().positive().optional(),
    defaultCreativeId: UuidSchema.nullable().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    icon: IconNameSchema.nullable().optional(),
    cpm: z.number().min(0).optional(),
    dailyFrequency: z.number().int().positive().nullable().optional(),
    weeklyFrequency: z.number().int().positive().nullable().optional(),
    impressionsCap: z.number().int().positive().nullable().optional(),
    budgetCap: z.number().min(0).nullable().optional(),
    segmentCreatives: SegmentCreativesMapSchema.optional(),
});
export const SetCampaignCreativesSchema = z.object({
    segmentCreatives: SegmentCreativesMapSchema,
});
export const CampaignResponseSchema = CampaignBaseSchema;
export const CampaignWithReceiversSchema = CampaignBaseSchema.extend({
    totalReceivers: z.number(),
    uniqueReceivers: z.number(),
});
// Pagination envelope used by paged list endpoints. (PaginationQuerySchema in
// common.ts is the inbound query shape; this one is the outbound response.)
export const PaginationEnvelopeSchema = z.object({
    limit: z.number(),
    offset: z.number(),
    total: z.number().optional(),
    hasMore: z.boolean(),
});
export const CampaignListResponseSchema = z.object({
    data: z.array(CampaignWithReceiversSchema),
    pagination: PaginationEnvelopeSchema,
});
export const CampaignAnalyticsSchema = z.object({
    campaignId: UuidSchema,
    days: z.number(),
    totalServed: z.number(),
    uniqueDevices: z.number(),
    dailyBreakdown: z.array(z.object({
        date: z.string(),
        served: z.number(),
        uniqueDevices: z.number(),
    })),
    segmentBreakdown: z.array(z.object({
        segmentId: UuidSchema,
        served: z.number(),
        uniqueDevices: z.number(),
    })),
});
export const CampaignCostSchema = z.object({
    estimatedCost: z.number().nullable(),
    realCost: z.number().nullable(),
});
// Lazy-loaded per-campaign all-time receivers stats. Cached on the backend
// in a 10-minute LRU.
export const CampaignStatisticsSchema = z.object({
    totalReceivers: z.number(),
    uniqueReceivers: z.number(),
});
export const CampaignAnalyticsQuerySchema = z.object({
    days: z.string().optional().default("30"),
});
//# sourceMappingURL=campaign.js.map